/* T330 story sections — shared behaviour for the t330-story-* sections.
 * Loaded once (defer) from the first story section; everything is idempotent
 * so a duplicate include is harmless. No jQuery, no libraries.
 *
 * Responsibilities:
 *  - window.updateStoryGates(): re-evaluates [data-story-gate] visibility and
 *    [data-story-model] model-name text from the [data-product-config] JSON
 *    anchor. The server renders the correct initial state (Liquid sets the
 *    same `hidden` attributes), so the initial call is a harmless re-sync;
 *    the real consumer is the configurator's AJAX swap — reinitProduct() in
 *    series-configurator.js calls this after each swap (the anchor lives
 *    inside the swap region, the story sections live outside it).
 *  - Delegated click/keydown handlers: tabs, accordion, compare toggle
 *    (bound once, survive swaps — same pattern as __scSwapBound).
 *  - IntersectionObserver reveal (.t3s-io -> .t3s-in), reduced-motion aware.
 *  - IntersectionObserver video slots: muted looping clips that play on the
 *    way in and pause on the way out, with no player chrome.
 */
(function () {
  if (window.__t3sInit) return;
  window.__t3sInit = true;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // ------------------------------------------------------------------
  // Reveal-on-scroll: one shared observer. The WeakSet lets
  // updateStoryGates() re-register after a swap without double-observing
  // nodes that are already tracked.
  // ------------------------------------------------------------------
  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;
  var io = null;

  function registerIO() {
    var nodes = document.querySelectorAll('.t3s-io');
    var i;
    // Reduced motion or no IO support -> reveal everything immediately.
    if (prefersReducedMotion() || !('IntersectionObserver' in window) || !observed) {
      for (i = 0; i < nodes.length; i++) nodes[i].classList.add('t3s-in');
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('t3s-in');
          io.unobserve(entry.target); // one-shot reveal
        });
      }, { threshold: 0.15 });
    }
    for (i = 0; i < nodes.length; i++) {
      if (observed.has(nodes[i])) continue;
      observed.add(nodes[i]);
      io.observe(nodes[i]);
    }
  }

  function hiddenByAttribute(node, scope) {
    while (node && node !== scope) {
      if (node.hidden) return true;
      node = node.parentElement;
    }
    return !!(scope && scope.hidden);
  }

  // Give one <source>/<img> its real URLs. Split out of the visibility pass so
  // the group pre-hydrator below can reuse it without repeating the dance.
  function hydrateSourceEl(source) {
    var sourceSizes = source.getAttribute('data-story-sizes');
    if (sourceSizes) source.setAttribute('sizes', sourceSizes);
    source.setAttribute('srcset', source.getAttribute('data-story-srcset'));
    source.removeAttribute('data-story-srcset');
    source.removeAttribute('data-story-sizes');
  }

  // forceLoad is for panes that are display:none at this moment. A lazy image
  // in a hidden pane never intersects anything, so the browser does not fetch
  // it -- handing it a src warms nothing, which is the trap the group
  // pre-hydrator below fell into. Dropping loading="lazy" makes the request
  // unconditional. It must also drop the leading `auto` from sizes: that
  // keyword is only honoured on a lazy image, and on a display:none one it
  // resolves to 0px, which picks the 400w candidate and forces a second fetch
  // the moment the pane is revealed.
  function hydrateImageEl(img, forceLoad) {
    var replacement = img.cloneNode(true);
    var src = img.getAttribute('data-story-src');
    var srcset = img.getAttribute('data-story-srcset');
    var sizes = img.getAttribute('data-story-sizes');
    if (forceLoad && sizes) sizes = sizes.replace(/^\s*auto\s*,\s*/i, '');
    if (sizes) replacement.setAttribute('sizes', sizes);
    if (srcset) replacement.setAttribute('srcset', srcset);
    if (forceLoad) replacement.setAttribute('loading', 'eager');
    if (src) replacement.setAttribute('src', src);
    replacement.removeAttribute('data-story-src');
    replacement.removeAttribute('data-story-srcset');
    replacement.removeAttribute('data-story-sizes');
    img.replaceWith(replacement);
  }

  // Hydrate EVERY deferred image inside a switcher, hidden panes included.
  // The visibility pass below deliberately skips anything under [hidden], which
  // is right for gated blocks -- a 2CH page must not fetch the 3CH artwork --
  // and wrong for the panes of a tab group, where the reader reveals any of
  // them with one tap and the request only STARTED at that tap. Measured on
  // the T330T page: eight images carried no src at all, 1.35 MB between them,
  // every one beginning its download at the moment its tab was clicked
  // (Bill 2026-09-15).
  // Two different things wear [hidden] inside a switcher and they must not be
  // treated alike. A PANE is hidden because the reader has not tapped it yet:
  // warming it is the whole point. A GATE is hidden because this configuration
  // does not have the feature -- a 2CH page must never fetch the 3CH artwork,
  // no matter how close to the viewport it scrolls. Only the gate carries one
  // of these attributes, so that is the test.
  function inHiddenGate(el, stopAt) {
    var node = el;
    while (node && node !== stopAt) {
      if (node.hidden && (node.hasAttribute('data-story-gate') ||
        node.hasAttribute('data-story-model-gate') ||
        node.hasAttribute('data-story-model-exclude'))) return true;
      node = node.parentElement;
    }
    return false;
  }

  function hydrateGroup(root) {
    if (!root || root.__t3sGroupHydrated) return;
    root.__t3sGroupHydrated = true;
    var sources = root.querySelectorAll('picture source[data-story-srcset]');
    for (var gs = 0; gs < sources.length; gs++) {
      if (!inHiddenGate(sources[gs], root)) hydrateSourceEl(sources[gs]);
    }
    var images = root.querySelectorAll('img[data-story-src]');
    for (var gi = 0; gi < images.length; gi++) {
      // Gated-off content is left entirely to the visibility pass, which re-runs
      // whenever updateStoryGates changes a configuration.
      if (inHiddenGate(images[gi], root)) continue;
      // No box == the pane is display:none, so lazy would never fire. The pane
      // on screen keeps lazy and its sizes="auto", which resolves against a
      // real layout width and so picks a better candidate than any fallback.
      hydrateImageEl(images[gi], images[gi].getClientRects().length === 0);
    }
  }

  // One observer for every switcher on the page. Fires well ahead of the
  // module so the panes are already painted by the time a tab is tapped, and
  // unobserves immediately -- this is a one-shot warm-up, not a live binding.
  function watchSwitcherGroups() {
    var groups = document.querySelectorAll('.t3s [data-t3s-tabs], .t3s [data-t3s-diagrams], .t3s [data-t3s-acc]');
    if (!groups.length) return;
    if (!('IntersectionObserver' in window)) {
      for (var f = 0; f < groups.length; f++) hydrateGroup(groups[f]);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (!entries[e].isIntersecting) continue;
        hydrateGroup(entries[e].target);
        io.unobserve(entries[e].target);
      }
    }, { rootMargin: '900px 0px' });
    for (var g = 0; g < groups.length; g++) io.observe(groups[g]);
  }

  function hydrateVisibleStoryImages() {
    // <picture> sources first: the browser runs its selection algorithm when the
    // img gets a src, and a source that is still empty at that moment loses.
    var sources = document.querySelectorAll('.t3s picture source[data-story-srcset]');
    for (var s = 0; s < sources.length; s++) {
      var source = sources[s];
      if (hiddenByAttribute(source, document.documentElement)) continue;
      hydrateSourceEl(source);
    }
    var images = document.querySelectorAll('.t3s img[data-story-src]');
    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      if (hiddenByAttribute(img, document.documentElement)) continue;
      hydrateImageEl(img);
    }
  }
  window.hydrateVisibleStoryImages = hydrateVisibleStoryImages;

  function syncGateGroups() {
    var gateGroups = document.querySelectorAll('.t3s [data-story-gate-group]');
    for (var g = 0; g < gateGroups.length; g++) {
      var groupChildren = gateGroups[g].children;
      var hasVisibleChild = false;
      for (var c = 0; c < groupChildren.length; c++) {
        if ((groupChildren[c].hasAttribute('data-story-gate') || groupChildren[c].hasAttribute('data-story-model-gate')) && !groupChildren[c].hidden) {
          hasVisibleChild = true;
          break;
        }
      }
      gateGroups[g].hidden = !hasVisibleChild;
    }
  }

  // ------------------------------------------------------------------
  // Layout-diagram switcher. The dashes stay the indicator; these give the
  // panes a second way in.
  // ------------------------------------------------------------------
  function setDiagramPane(wrap, idx) {
    var panes = wrap.querySelectorAll('.t3s-diagram');
    var dots = wrap.querySelectorAll('.t3s-diagswitch__dot');
    for (var p = 0; p < panes.length; p++) panes[p].classList.toggle('on', p === idx);
    for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('on', d === idx);
  }

  // Step to the next/previous pane, skipping any the capability gates have
  // hidden — a 2CH page carries the 3CH diagram in the DOM with [hidden] on it,
  // and swiping onto a blank pane would look broken. Wraps around: three panes
  // is short enough that a dead end at each end is just friction.
  function stepDiagram(wrap, dir) {
    var panes = wrap.querySelectorAll('.t3s-diagram');
    var visible = [];
    var current = -1;
    for (var i = 0; i < panes.length; i++) {
      if (panes[i].hidden) continue;
      visible.push(i);
      if (panes[i].classList.contains('on')) current = i;
    }
    if (visible.length < 2) return;
    var at = visible.indexOf(current);
    if (at < 0) at = 0;
    setDiagramPane(wrap, visible[(at + dir + visible.length) % visible.length]);
  }

  // Swipe. The dashes are a 28x3px control — right as an indicator, poor as the
  // ONLY way to change pane with a thumb (Bill 2026-09-15). Listeners are
  // passive and nothing is preventDefault()ed: the gesture has to be clearly
  // horizontal (40px, and half again as much across as down) before it counts,
  // so scrolling the page through this module behaves exactly as before.
  var swipeWrap = null, swipeX = 0, swipeY = 0;
  document.addEventListener('touchstart', function (e) {
    swipeWrap = null;
    if (!e.touches || e.touches.length !== 1 || !e.target.closest) return;
    var w = e.target.closest('[data-t3s-diagrams]');
    if (!w) return;
    swipeWrap = w;
    swipeX = e.touches[0].clientX;
    swipeY = e.touches[0].clientY;
  }, { passive: true });
  document.addEventListener('touchend', function (e) {
    var w = swipeWrap;
    swipeWrap = null;
    if (!w || !e.changedTouches || e.changedTouches.length !== 1) return;
    var dx = e.changedTouches[0].clientX - swipeX;
    var dy = e.changedTouches[0].clientY - swipeY;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    stepDiagram(w, dx < 0 ? 1 : -1);
  }, { passive: true });

  // "T330T 3CH" -> base "T330T" + channel "3CH", so the phone hero can hang the
  // channel code off the wordmark as a pill the way the desktop artwork paints
  // it. Same rule as the Liquid that renders the first paint: split only when
  // the last word is digits + CH, otherwise the name prints whole -- a model
  // that is not "<name> <n>CH" must not lose its last word into a badge.
  // The badge span is created on demand because a swap can move a page from a
  // name without a channel code to one with it.
  function applyModelSplit(el, model) {
    var name = String(model || '').trim();
    var parts = name.match(/^(.*\S)\s+(\d+CH)$/i);
    var base = el.querySelector('[data-story-model-base]');
    if (!base) { el.textContent = name; return; } // not the split shape; leave it as a plain title
    var chip = el.querySelector('[data-story-model-ch]');
    base.textContent = parts ? parts[1] : name;
    if (!parts) {
      if (chip) chip.hidden = true;
      el.classList.remove('t3s-hero__model');
      return;
    }
    if (!chip) {
      chip = document.createElement('span');
      chip.className = 't3s-hero__ch';
      chip.setAttribute('data-story-model-ch', '');
      el.appendChild(chip);
    }
    chip.textContent = parts[2].toUpperCase();
    chip.hidden = false;
    el.classList.add('t3s-hero__model');
  }

  // ------------------------------------------------------------------
  // Camera gating + model-name sync. Exposed globally so
  // series-configurator.js reinitProduct() can call it after a swap.
  // Gate values: always | 1ch | 2ch | 3ch | 4ch |
  //              cabin | cabin_count_1 | cabin_count_2 |
  //              telephoto | telephoto_count_1 | telephoto_count_2 |
  //              waterproof | waterproof_single | dual_waterproof |
  //              not_dual_waterproof | normal_rear |
  //              3ch_no_telephoto | 3ch_no_telephoto_no_dual_waterproof |
  //              cabin_or_2ch | not_2ch | not_telephoto
  //              2ch_cabin | 2ch_normal_rear
  // Capabilities are resolved server-side from the canonical product-model map.
  // ------------------------------------------------------------------
  // The one place a series is enabled for the story runtime.
  //
  // This used to be two hard-coded lists — one guard in storyCapabilities and
  // one matcher in storyScopeMatches — and adding A149 Pro to only the second
  // left the first rejecting it, so the fix was dead code and the page stayed
  // blank. Both now read this map, so a series is either registered or it is
  // not; there is no half-registered state to get wrong.
  //
  // Value = what the section's data-story-scope is compared against:
  //   'template' — one template serves every config in the series
  //   'model'    — each config has its own scope string (T340's shape)
  //
  // A series missing here is not an error anywhere: storyCapabilities returns
  // null, updateStoryGates hides every .t3s root, and the page renders blank
  // with no console output. Register a new series HERE and in the Liquid
  // capability registry, and verify in a browser, not only in the HTML.
  var STORY_SERIES = {
    'T330': 'template',
    'T340': 'model',
    'A329S': 'template',
    'A149 Pro': 'template',
  };

  function storyCapabilities(cfg) {
    var value = cfg && cfg.capabilities;
    if (!value || value.known !== true || value.status !== 'active' || value.storyReady !== true || typeof cfg.productModel !== 'string') return null;
    if (value.model !== cfg.productModel || value.series !== cfg.series) return null;
    if (!STORY_SERIES[value.series]) return null;
    if (typeof value.expectedTemplate !== 'string' || value.expectedTemplate === '') return null;

    var channel = Number(value.channel);
    var cabinCount = Number(value.cabinCount);
    var telephotoCount = Number(value.telephotoCount);
    var waterproofCameraCount = Number(value.waterproofCameraCount);
    var normalRearCount = Number(value.normalRearCount);
    var counts = [channel, cabinCount, telephotoCount, waterproofCameraCount, normalRearCount];
    for (var i = 0; i < counts.length; i++) {
      if (!Number.isInteger(counts[i]) || counts[i] < 0) return null;
    }
    if (channel < 1 || channel > 4) return null;
    if (1 + cabinCount + telephotoCount + waterproofCameraCount + normalRearCount !== channel) return null;

    return {
      model: value.model,
      series: value.series,
      expectedTemplate: value.expectedTemplate,
      channel: channel,
      cabinCount: cabinCount,
      telephotoCount: telephotoCount,
      normalRearCount: normalRearCount,
      cabin: cabinCount > 0,
      telephoto: telephotoCount > 0,
      waterproofCameraCount: waterproofCameraCount
    };
  }

  function storyGateMatches(gate, capabilities) {
    if (!capabilities || typeof gate !== 'string') return false;
    if (gate === 'always') return true;
    if (gate === '1ch') return capabilities.channel === 1;
    if (gate === '2ch') return capabilities.channel === 2;
    if (gate === '3ch') return capabilities.channel === 3;
    if (gate === '4ch') return capabilities.channel === 4;
    if (gate === 'cabin_count_1') return capabilities.cabinCount === 1;
    if (gate === 'cabin_count_2') return capabilities.cabinCount === 2;
    if (gate === 'telephoto_count_1') return capabilities.telephotoCount === 1;
    if (gate === 'telephoto_count_2') return capabilities.telephotoCount === 2;
    if (gate === 'normal_rear') return capabilities.normalRearCount > 0;
    if (gate === '3ch_no_telephoto') return capabilities.channel === 3 && capabilities.telephotoCount === 0;
    if (gate === '3ch_no_telephoto_no_dual_waterproof') {
      return capabilities.channel === 3 && capabilities.telephotoCount === 0 && capabilities.waterproofCameraCount !== 2;
    }
    if (gate === 'dual_waterproof') return capabilities.waterproofCameraCount === 2;
    if (gate === 'not_dual_waterproof') return capabilities.waterproofCameraCount !== 2;
    if (gate === 'waterproof_single') return capabilities.waterproofCameraCount === 1;
    if (gate === 'cabin_or_2ch') return capabilities.channel === 2 || capabilities.cabinCount > 0;
    if (gate === '2ch_cabin') return capabilities.channel === 2 && capabilities.cabinCount > 0;
    if (gate === '2ch_normal_rear') return capabilities.channel === 2 && capabilities.normalRearCount > 0;
    if (gate === 'not_2ch') return capabilities.channel !== 2;
    if (gate === 'not_telephoto') return capabilities.telephotoCount === 0;
    if (gate === 'cabin') return capabilities.cabinCount > 0;
    if (gate === 'telephoto') return capabilities.telephotoCount > 0;
    if (gate === 'waterproof') return capabilities.waterproofCameraCount > 0;
    return false;
  }

  function storyScopeMatches(root, capabilities) {
    if (!root || !capabilities) return false;
    var scope = root.getAttribute('data-story-scope');
    if (typeof scope !== 'string' || scope === '') return false;
    var rule = STORY_SERIES[capabilities.series];
    if (!rule) return false;
    return scope === (rule === 'model' ? capabilities.model : capabilities.expectedTemplate);
  }

  function updateStoryGates() {
    var anchor = document.querySelector('[data-product-config]');
    var cfg = null;
    if (anchor) {
      try { cfg = JSON.parse(anchor.textContent); } catch (e) { cfg = null; }
    }
    var capabilities = storyCapabilities(cfg);

    var roots = document.querySelectorAll('.t3s');
    for (var r = 0; r < roots.length; r++) {
      var hideRoot = !storyScopeMatches(roots[r], capabilities);
      if (hideRoot) pausePanelVideos(roots[r]);
      roots[r].hidden = hideRoot;
    }

    var gated = document.querySelectorAll('.t3s [data-story-gate]');
    for (var i = 0; i < gated.length; i++) {
      var gate = gated[i].getAttribute('data-story-gate');
      gated[i].hidden = !storyGateMatches(gate, capabilities);
    }

    if (capabilities && typeof cfg.productModel === 'string' && cfg.productModel !== '') {
      var models = document.querySelectorAll('.t3s [data-story-model]');
      for (var j = 0; j < models.length; j++) models[j].textContent = cfg.productModel;

      // The phone hero prints the channel code as a pill riding the wordmark
      // rather than as part of one string, so it cannot use the assignment
      // above -- textContent on the <h2> would flatten the two spans into one
      // text node and the badge would never come back. Rebuilt instead.
      var splitModels = document.querySelectorAll('.t3s [data-story-model-split]');
      for (var sm = 0; sm < splitModels.length; sm++) applyModelSplit(splitModels[sm], cfg.productModel);

      var modelGates = document.querySelectorAll('.t3s [data-story-model-gate]');
      for (var k = 0; k < modelGates.length; k++) {
        modelGates[k].hidden = modelGates[k].getAttribute('data-story-model-gate') !== cfg.productModel;
      }

      var modelExcludes = document.querySelectorAll('.t3s [data-story-model-exclude]');
      for (var x = 0; x < modelExcludes.length; x++) {
        modelExcludes[x].hidden = modelExcludes[x].getAttribute('data-story-model-exclude') === cfg.productModel;
      }
    } else {
      var invalidModelGates = document.querySelectorAll('.t3s [data-story-model-gate],.t3s [data-story-model-exclude]');
      for (var y = 0; y < invalidModelGates.length; y++) invalidModelGates[y].hidden = true;
    }

    syncStoryUi();
  }
  window.updateStoryGates = updateStoryGates;

  // ------------------------------------------------------------------
  // Accordion group housekeeping, re-run after every gate pass:
  //  - if the OPEN item was gated away, open the first visible item;
  //  - one visible item -> .t3s-acc--single (plain block: no chevron, no
  //    toggle) with that item forced open;
  //  - the shared media pane follows the open item ([data-acc-pane] keyed
  //    panes and/or the legacy data-media-src img swap).
  // ------------------------------------------------------------------
  var accStates = typeof WeakMap === 'function' ? new WeakMap() : null;

  function accState(group) {
    if (!accStates) {
      if (!group.__t3sAccState) group.__t3sAccState = { revision: 0, cleanupTimer: 0, cleanupBody: null, cleanupHandler: null };
      return group.__t3sAccState;
    }
    var state = accStates.get(group);
    if (!state) {
      state = { revision: 0, cleanupTimer: 0, cleanupBody: null, cleanupHandler: null };
      accStates.set(group, state);
    }
    return state;
  }

  function accItemKey(group, item) {
    var key = item.getAttribute('data-acc-key');
    if (key) return key;
    var items = group.querySelectorAll('.t3s-acc__item');
    for (var i = 0; i < items.length; i++) {
      if (items[i] !== item) continue;
      key = 't3s-acc-item-' + i;
      item.setAttribute('data-acc-key', key);
      return key;
    }
    return '';
  }

  function stageAccImageData(image, item) {
    var mediaSrc = item.getAttribute('data-media-src');
    var mediaSrcset = item.getAttribute('data-media-srcset');
    var mediaSizes = item.getAttribute('data-media-sizes');
    var mediaAlt = item.getAttribute('data-media-alt');
    var mediaWidth = item.getAttribute('data-media-width');
    var mediaHeight = item.getAttribute('data-media-height');
    image.removeAttribute('src');
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    if (mediaSrc) image.setAttribute('data-story-src', mediaSrc); else image.removeAttribute('data-story-src');
    if (mediaSrcset) image.setAttribute('data-story-srcset', mediaSrcset); else image.removeAttribute('data-story-srcset');
    if (mediaSizes) image.setAttribute('data-story-sizes', mediaSizes); else image.removeAttribute('data-story-sizes');
    image.setAttribute('alt', mediaAlt || '');
    if (mediaWidth) image.setAttribute('width', mediaWidth); else image.removeAttribute('width');
    if (mediaHeight) image.setAttribute('height', mediaHeight); else image.removeAttribute('height');
  }

  function ensureLegacyAccPanes(group) {
    var media = group.querySelector('.t3s-acc__media');
    if (!media || media.querySelector('[data-acc-pane]')) return;
    var sourceImage = null;
    for (var c = 0; c < media.children.length; c++) {
      if (media.children[c].tagName === 'IMG') { sourceImage = media.children[c]; break; }
    }
    if (!sourceImage) return;
    var items = group.querySelectorAll('.t3s-acc__item');
    var fragment = document.createDocumentFragment();
    var count = 0;
    for (var i = 0; i < items.length; i++) {
      if (!items[i].getAttribute('data-media-src')) continue;
      var pane = document.createElement('div');
      pane.setAttribute('data-acc-pane', accItemKey(group, items[i]));
      var image = sourceImage.cloneNode(true);
      stageAccImageData(image, items[i]);
      pane.appendChild(image);
      fragment.appendChild(pane);
      count++;
    }
    if (!count) return;
    while (media.firstChild) media.removeChild(media.firstChild);
    media.appendChild(fragment);
  }

  function accItemByKey(items, key) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].getAttribute('data-acc-key') === key) return items[i];
    }
    return null;
  }

  function syncAccPane(group, item, options) {
    options = options || {};
    ensureLegacyAccPanes(group);
    var key = item ? item.getAttribute('data-acc-key') : '';
    if (item && !key && item.getAttribute('data-media-src')) key = accItemKey(group, item);
    var panes = group.querySelectorAll('.t3s-acc__media [data-acc-pane]');
    var items = group.querySelectorAll('.t3s-acc__item');
    for (var i = 0; i < panes.length; i++) {
      var paneKey = panes[i].getAttribute('data-acc-pane');
      var owner = accItemByKey(items, paneKey);
      var selected = !!key && paneKey === key && !!owner && !owner.hidden;
      if (!selected && panes[i].classList.contains('on')) pausePanelVideos(panes[i]);
      panes[i].classList.toggle('on', selected);
      panes[i].hidden = !selected;
    }
    if (options.hydrate !== false) hydrateVisibleStoryImages();
  }

  function releaseAccBodyHeight(body, item, state, revision) {
    function finishAccHeight() {
      if (state.cleanupTimer) clearTimeout(state.cleanupTimer);
      body.removeEventListener('transitionend', onAccHeightEnd);
      state.cleanupTimer = 0;
      state.cleanupBody = null;
      state.cleanupHandler = null;
      if (state.revision === revision && item.classList.contains('open')) body.style.height = 'auto';
    }
    function onAccHeightEnd(e) {
      if (e.target === body && e.propertyName === 'height') finishAccHeight();
    }
    state.cleanupBody = body;
    state.cleanupHandler = onAccHeightEnd;
    body.addEventListener('transitionend', onAccHeightEnd);
    state.cleanupTimer = setTimeout(finishAccHeight, 340);
  }

  function setOpenAccItem(group, item, options) {
    options = options || {};
    var state = accState(group);
    if (state.cleanupTimer) clearTimeout(state.cleanupTimer);
    if (state.cleanupBody && state.cleanupHandler) state.cleanupBody.removeEventListener('transitionend', state.cleanupHandler);
    state.cleanupTimer = 0;
    state.cleanupBody = null;
    state.cleanupHandler = null;
    var revision = ++state.revision;
    var items = group.querySelectorAll('.t3s-acc__item');
    var bodies = [];
    var heights = [];
    var animate = options.animate !== false && !prefersReducedMotion();
    for (var b = 0; b < items.length; b++) {
      var accBody = items[b].querySelector('.t3s-acc__body');
      bodies.push(accBody);
      heights.push(accBody ? accBody.getBoundingClientRect().height : 0);
      if (animate && accBody) accBody.style.height = heights[b] + 'px';
    }
    for (var i = 0; i < items.length; i++) {
      var isOpen = items[i] === item;
      items[i].classList.toggle('open', isOpen);
      var head = items[i].querySelector('.t3s-acc__head');
      if (head) head.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      var body = bodies[i];
      if (body) body.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    }
    if (animate) {
      void group.offsetHeight;
      for (var a = 0; a < items.length; a++) {
        if (!bodies[a]) continue;
        if (items[a] === item) {
          var targetHeight = bodies[a].scrollHeight;
          bodies[a].style.height = targetHeight + 'px';
          if (Math.abs(heights[a] - targetHeight) < 0.5) bodies[a].style.height = 'auto';
          else releaseAccBodyHeight(bodies[a], items[a], state, revision);
        } else {
          bodies[a].style.height = '0px';
        }
      }
    } else {
      for (var d = 0; d < items.length; d++) {
        if (bodies[d]) bodies[d].style.height = items[d] === item ? 'auto' : '0px';
      }
    }
    syncAccPane(group, item, { hydrate: options.hydrate !== false });
  }

  function syncAccGroups() {
    var groups = document.querySelectorAll('[data-t3s-acc]');
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var items = group.querySelectorAll('.t3s-acc__item');
      var visible = [];
      for (var i = 0; i < items.length; i++) {
        if (!items[i].hidden) visible.push(items[i]);
      }
      group.classList.toggle('t3s-acc--single', visible.length === 1);
      if (!visible.length) {
        syncAccPane(group, null, { hydrate: false });
        continue;
      }
      var open = null;
      for (var v = 0; v < visible.length; v++) {
        if (visible[v].classList.contains('open')) { open = visible[v]; break; }
      }
      if (!open) { // open item hidden by gating (or none yet): fall back to first visible
        open = visible[0];
      }
      setOpenAccItem(group, open, { animate: false, hydrate: false });
    }
  }

  // Layout-diagram groups (dash switcher): if the active pane was gated
  // away, fall back to the first visible pane; a pane's dash hides with it.
  function syncDiagGroups() {
    var wraps = document.querySelectorAll('[data-t3s-diagrams]');
    for (var w = 0; w < wraps.length; w++) {
      var panes = wraps[w].querySelectorAll('.t3s-diagram');
      var dots = wraps[w].querySelectorAll('.t3s-diagswitch__dot');
      var active = -1, firstVis = -1;
      for (var i = 0; i < panes.length; i++) {
        if (dots[i]) dots[i].hidden = panes[i].hidden;
        if (!panes[i].hidden && firstVis < 0) firstVis = i;
        if (!panes[i].hidden && panes[i].classList.contains('on')) active = i;
      }
      if (active < 0 && firstVis >= 0) {
        for (var j = 0; j < panes.length; j++) panes[j].classList.toggle('on', j === firstVis);
        for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('on', d === firstVis);
      }
    }
  }

  // ------------------------------------------------------------------
  // Helpers for the delegated handlers
  // ------------------------------------------------------------------
  var PILL_SCOPE = '[data-t3s-tabs],[data-t3s-toggle]';
  var indicatorObserved = typeof WeakSet === 'function' ? new WeakSet() : null;
  var indicatorObserver = null;
  var pendingIndicatorGroups = [];
  var indicatorFrame = 0;

  function directPills(scope) {
    var all = scope.querySelectorAll('.t3s-tab');
    var pills = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].closest(PILL_SCOPE) === scope) pills.push(all[i]);
    }
    return pills;
  }

  function activePrimaryPill(group) {
    var pills = directPills(group);
    for (var i = 0; i < pills.length; i++) {
      if (pills[i].classList.contains('on') && pills[i].parentElement.classList.contains('t3s-tabs--primary')) return pills[i];
    }
    return null;
  }

  function syncPrimaryIndicator(group, tab, animate) {
    var rail = tab && tab.parentElement;
    if (!rail || !rail.classList.contains('t3s-tabs--primary')) return;
    var width = tab.offsetWidth;
    if (!width) {
      rail.classList.remove('t3s-tabs--indicator-ready', 't3s-tabs--indicator-live');
      return;
    }
    var shouldAnimate = animate && !prefersReducedMotion() && rail.classList.contains('t3s-tabs--indicator-ready');
    rail.classList.toggle('t3s-tabs--indicator-live', shouldAnimate);
    rail.style.setProperty('--t3s-indicator-x', tab.offsetLeft + 'px');
    rail.style.setProperty('--t3s-indicator-width', width + 'px');
    rail.classList.add('t3s-tabs--indicator-ready');
    if (!shouldAnimate && !prefersReducedMotion()) {
      requestAnimationFrame(function () {
        if (rail.isConnected) rail.classList.add('t3s-tabs--indicator-live');
      });
    }
  }

  function queuePrimaryIndicator(group) {
    if (!group || pendingIndicatorGroups.indexOf(group) !== -1) return;
    pendingIndicatorGroups.push(group);
    if (indicatorFrame) return;
    indicatorFrame = requestAnimationFrame(function () {
      var groups = pendingIndicatorGroups.slice();
      pendingIndicatorGroups.length = 0;
      indicatorFrame = 0;
      for (var i = 0; i < groups.length; i++) {
        if (!groups[i].isConnected) continue;
        syncPrimaryIndicator(groups[i], activePrimaryPill(groups[i]), false);
        syncRailEdges(groups[i].querySelector('.t3s-tabs--primary'));
      }
    });
  }

  // A pill rail that overflows says nothing about it: at 390px the parking rail
  // is 1178px of content in a 350px window, so a reader sees one clipped word at
  // the right edge and no reason to think it can be dragged. Flag whether it
  // overflows and which end it is against, and let the stylesheet fade the edge
  // that still has content behind it. Nothing here reads layout on the window
  // scroll path — only on this rail's own scroll, which fires while a finger is
  // actually on it.
  function syncRailEdges(rail) {
    if (!rail) return;
    var slack = rail.scrollWidth - rail.clientWidth;
    if (slack <= 1) {
      rail.setAttribute('data-t3s-overflow', 'false');
      rail.removeAttribute('data-t3s-edge');
      return;
    }
    rail.setAttribute('data-t3s-overflow', 'true');
    var atStart = rail.scrollLeft <= 1;
    var atEnd = rail.scrollLeft >= slack - 1;
    rail.setAttribute('data-t3s-edge', atStart && atEnd ? 'none' : atStart ? 'start' : atEnd ? 'end' : 'middle');
  }

  function observePrimaryIndicator(group) {
    var rail = group.querySelector('.t3s-tabs--primary');
    if (!rail || !indicatorObserved || !('ResizeObserver' in window)) return;
    if (!indicatorObserver) {
      indicatorObserver = new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          queuePrimaryIndicator(entries[i].target.closest('[data-t3s-tabs]'));
        }
      });
    }
    var nodes = [rail];
    for (var i = 0; i < nodes.length; i++) {
      if (indicatorObserved.has(nodes[i])) continue;
      indicatorObserved.add(nodes[i]);
      indicatorObserver.observe(nodes[i]);
    }
  }

  function tabDirection(group, target) {
    var pills = directPills(group);
    var visible = [];
    var current = null;
    for (var i = 0; i < pills.length; i++) {
      if (hiddenByAttribute(pills[i], group)) continue;
      visible.push(pills[i]);
      if (pills[i].classList.contains('on')) current = pills[i];
    }
    var from = visible.indexOf(current);
    var to = visible.indexOf(target);
    if (from < 0 || to < 0 || from === to) return 0;
    return to > from ? 1 : -1;
  }

  function pausePanelVideos(panel) {
    var videos = panel.querySelectorAll('video');
    for (var i = 0; i < videos.length; i++) videos[i].pause();
  }

  function setActivePill(scope, tab) {
    // Only reset pills that BELONG to this scope: a compare toggle nested
    // inside a tab panel keeps its own pill state when the outer bar changes.
    var pills = scope.querySelectorAll('.t3s-tab');
    var isTablist = scope.hasAttribute('data-t3s-tabs');
    for (var i = 0; i < pills.length; i++) {
      if (pills[i].closest(PILL_SCOPE) !== scope) continue;
      pills[i].classList.remove('on');
      if (isTablist) {
        pills[i].setAttribute('aria-selected', 'false');
        pills[i].tabIndex = -1;
      } else {
        pills[i].removeAttribute('aria-selected');
        pills[i].setAttribute('aria-pressed', 'false');
        pills[i].removeAttribute('tabindex');
      }
    }
    tab.classList.add('on');
    if (isTablist) {
      tab.setAttribute('aria-selected', 'true');
      tab.tabIndex = 0;
    } else {
      tab.setAttribute('aria-pressed', 'true');
    }
  }

  function centerActivePill(tab, smooth) {
    var rail = tab && tab.parentElement;
    if (!rail || !rail.classList.contains('t3s-tabs--primary')) return;
    if (rail.scrollWidth <= rail.clientWidth + 1) return;

    var railRect = rail.getBoundingClientRect();
    var tabRect = tab.getBoundingClientRect();
    var target = rail.scrollLeft + (tabRect.left - railRect.left) - ((rail.clientWidth - tabRect.width) / 2);
    target = Math.max(0, Math.min(target, rail.scrollWidth - rail.clientWidth));
    var behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto';
    if (typeof rail.scrollTo === 'function') rail.scrollTo({ left: target, behavior: behavior });
    else rail.scrollLeft = target;
  }

  function restartPanelAnimation(panel, direction) {
    panel.classList.remove('t3s-panel--entering');
    if (direction > 0) panel.setAttribute('data-t3s-enter', 'next');
    else if (direction < 0) panel.setAttribute('data-t3s-enter', 'previous');
    else panel.removeAttribute('data-t3s-enter');
    if (prefersReducedMotion()) return;
    void panel.offsetWidth;
    panel.classList.add('t3s-panel--entering');
  }

  function activateTabGroup(group, tab, options) {
    options = options || {};
    var changed = !tab.classList.contains('on');
    var direction = changed ? tabDirection(group, tab) : 0;
    var key = tab.getAttribute('data-tab');
    var panels = group.querySelectorAll('.t3s-panel');
    var activePanel = null;

    setActivePill(group, tab);
    for (var i = 0; i < panels.length; i++) {
      if (panels[i].closest('[data-t3s-tabs]') !== group) continue;
      var selected = panels[i].getAttribute('data-panel') === key;
      if (!selected && !panels[i].hidden) pausePanelVideos(panels[i]);
      panels[i].hidden = !selected;
      if (selected) activePanel = panels[i];
      else panels[i].classList.remove('t3s-panel--entering');
    }
    syncPrimaryIndicator(group, tab, changed && options.animate !== false);
    if (changed && options.animate !== false && activePanel) restartPanelAnimation(activePanel, direction);
    if (options.center !== false) centerActivePill(tab, options.smooth === true);
    if (options.hydrate !== false) hydrateVisibleStoryImages();
  }

  function syncTabGroups() {
    var groups = document.querySelectorAll('[data-t3s-tabs]');
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var pills = group.querySelectorAll('.t3s-tab');
      var panels = group.querySelectorAll('.t3s-panel');
      var rail = group.querySelector('.t3s-tabs--primary');
      var visiblePillCount = 0;
      var active = null;
      var fallback = null;
      for (var i = 0; i < pills.length; i++) {
        var pill = pills[i];
        if (pill.closest(PILL_SCOPE) !== group) continue;
        pill.setAttribute('role', 'tab');
        if (!pill.id) pill.id = 't3s-tab-' + g + '-' + i;
        var key = pill.getAttribute('data-tab');
        var matchingPanel = null;
        for (var p = 0; p < panels.length; p++) {
          if (panels[p].closest('[data-t3s-tabs]') === group && panels[p].getAttribute('data-panel') === key) {
            matchingPanel = panels[p];
            break;
          }
        }
        if (!matchingPanel) continue;
        if (!matchingPanel.id) matchingPanel.id = 't3s-panel-' + g + '-' + i;
        matchingPanel.setAttribute('role', 'tabpanel');
        matchingPanel.setAttribute('aria-labelledby', pill.id);
        matchingPanel.tabIndex = 0;
        pill.setAttribute('aria-controls', matchingPanel.id);
        // panel.hidden belongs to the previous tab state; only a pill or
        // ancestor gate makes this key ineligible as the visible fallback.
        if (hiddenByAttribute(pill, group) || hiddenByAttribute(matchingPanel.parentElement, group)) continue;
        visiblePillCount++;
        if (!fallback) fallback = pill;
        if (pill.classList.contains('on')) active = pill;
      }
      if (rail) rail.setAttribute('data-visible-tabs', String(visiblePillCount));
      observePrimaryIndicator(group);
      // Gating changes the visible pill count without changing the rail's own
      // box, so scrollWidth can move while the ResizeObserver stays silent and a
      // stale edge shadow lingers until the first drag. Re-read it here, where
      // the visible set has just been decided.
      syncRailEdges(rail);
      var chosen = active || fallback;
      if (!chosen) continue;
      activateTabGroup(group, chosen, { animate: false, smooth: false, hydrate: false });
    }
  }

  function syncToggleGroups() {
    var groups = document.querySelectorAll('[data-t3s-toggle]');
    for (var g = 0; g < groups.length; g++) {
      var pills = groups[g].querySelectorAll('.t3s-tab');
      var active = null;
      var fallback = null;
      for (var i = 0; i < pills.length; i++) {
        if (pills[i].closest(PILL_SCOPE) !== groups[g]) continue;
        if (!fallback) fallback = pills[i];
        if (pills[i].classList.contains('on')) active = pills[i];
      }
      if (active || fallback) setActivePill(groups[g], active || fallback);
    }
  }

  // ------------------------------------------------------------------
  // Sticky-bar contrast.
  // The theme's product tab bar (.product-detail-tab) pins to the top of the
  // viewport at #f5f5f7. Across ~28,000px of pure-black story that reads as a
  // bright band sitting on the page, and it is worst over the full-bleed lens
  // scenes, where it cuts a deliberately cinematic frame in half.
  //
  // The bar lives in sections/product-tab.liquid — a site-wide component this
  // module does not own and must not fork — so instead of touching it we flag
  // <html> while a story section is genuinely behind it, and t330-story.css
  // scopes every override to that flag. Both of those assets are loaded only by
  // t330-story-protection.liquid, so no non-story product page can be affected
  // even in principle.
  //
  // Shrinking the observer root to the bar's own band makes "intersecting" mean
  // "behind the bar", which keeps this free of any per-scrolled-frame work.
  // Gated-away sections are [hidden] and so never intersect — no special case.
  // ------------------------------------------------------------------
  var CHROME_CLASS = 't3s-dark-chrome';
  var chromeObserver = null;
  var chromeTracked = [];
  var chromeGeometry = '';

  function applyChromeFlag() {
    var on = false;
    for (var i = 0; i < chromeTracked.length; i++) {
      if (chromeTracked[i].behind) { on = true; break; }
    }
    document.documentElement.classList.toggle(CHROME_CLASS, on);
  }

  // Every path that stops tracking must also stop OBSERVING. Returning early
  // used to leave the observer live and chromeTracked pointing at the old nodes:
  // deleting the last story section in Theme Editor left it holding detached
  // nodes for the rest of the session, and the stale geometry key could make a
  // later re-add return early instead of rebuilding.
  function teardownChromeObserver() {
    if (chromeObserver) chromeObserver.disconnect();
    chromeObserver = null;
    chromeTracked = [];
    chromeGeometry = '';
    document.documentElement.classList.remove(CHROME_CLASS);
  }

  function registerChromeObserver() {
    var bar = document.querySelector('.product-detail-tab');
    // Light-themed sections are excluded: the flag exists to make the sticky
    // bar legible over a BLACK story ground, and over a light one it would
    // invert the bar for no reason.
    var roots = document.querySelectorAll('.t3s:not([data-t3s-theme="light"])');
    if (!bar || !roots.length || !('IntersectionObserver' in window)) {
      teardownChromeObserver();
      return;
    }
    var barHeight = Math.round(bar.getBoundingClientRect().height);
    var viewport = window.innerHeight;
    // Deliberately NOT a teardown: a zero measurement here means the page is
    // mid-layout, not that the story is gone. Disconnecting a working observer
    // on a transient bad measurement would leave recovery to the next resize.
    // This path writes no geometry key, so the rebuild test below still fires
    // correctly on the next call.
    if (!barHeight || !viewport) return;

    // The band moves with the bar's own height (68px desktop / 89px mobile) and
    // with the viewport, and the section list changes when the configurator
    // swaps a config in. Rebuild only when one of those actually changed.
    // Node identity is part of the key, not just the count. The Theme Editor can
    // replace a section with fresh DOM while the count and geometry stay
    // identical; keyed on numbers alone this returned early and the observer kept
    // watching detached nodes, so the replacement never drove .t3s-dark-chrome.
    var sameNodes = chromeTracked.length === roots.length;
    if (sameNodes) {
      for (var n = 0; n < roots.length; n++) {
        if (chromeTracked[n].el !== roots[n]) { sameNodes = false; break; }
      }
    }
    var geometry = barHeight + 'x' + viewport + 'x' + roots.length;
    if (chromeObserver && sameNodes && geometry === chromeGeometry) return;
    chromeGeometry = geometry;
    if (chromeObserver) chromeObserver.disconnect();

    chromeTracked = [];
    for (var i = 0; i < roots.length; i++) chromeTracked.push({ el: roots[i], behind: false });

    chromeObserver = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        for (var t = 0; t < chromeTracked.length; t++) {
          if (chromeTracked[t].el === entries[e].target) {
            chromeTracked[t].behind = entries[e].isIntersecting;
            break;
          }
        }
      }
      applyChromeFlag();
    }, { rootMargin: '0px 0px -' + Math.max(0, viewport - barHeight) + 'px 0px', threshold: 0 });

    for (var j = 0; j < chromeTracked.length; j++) chromeObserver.observe(chromeTracked[j].el);
  }

  function syncStoryUi() {
    syncGateGroups(); // model gates must settle before wrappers are measured
    syncTabGroups(); // active conditional tab may have been gated away
    syncToggleGroups(); // expose the initial comparison state to assistive tech
    syncAccGroups(); // gating may have hidden the open item / left a single item
    syncDiagGroups(); // the active layout pane may have been gated away
    hydrateVisibleStoryImages();
    watchSwitcherGroups();
    registerIO(); // pick up any .t3s-io nodes added since the last pass
    registerVideoAutoplay(); // ditto for video slots revealed by this pass
    registerChromeObserver(); // sections may have been swapped or re-gated
  }

  function queueAllPrimaryIndicators() {
    var groups = document.querySelectorAll('[data-t3s-tabs]');
    for (var i = 0; i < groups.length; i++) queuePrimaryIndicator(groups[i]);
  }

  // ------------------------------------------------------------------
  // Video slots: muted, looping, chrome-free clips that run only while they
  // are on screen. The <video> is built as the slot approaches the viewport,
  // so a page full of slots still costs zero video bytes until the reader
  // scrolls near one. Under prefers-reduced-motion nothing plays by itself —
  // the clip mounts with native controls so the reader starts it deliberately.
  //
  // data-video-url deliberately STAYS on the element after mounting: the CSS
  // uses it to suppress the "VIDEO COMING SOON" placeholder, and dropping it
  // would bring the placeholder back underneath the playing video.
  // ------------------------------------------------------------------
  var videoObserved = typeof WeakSet === 'function' ? new WeakSet() : null;
  var videoMountIO = null; // margin-expanded: build the element just before it arrives
  var videoPlayIO = null; // true viewport: decide play vs pause
  var VIDEO_PLAY_RATIO = 0.25;

  // Give the reader native controls. Used wherever nothing is going to play by
  // itself: reduced motion, no observer, and — the case that actually bites —
  // a browser that refuses to autoplay (iOS Low Power Mode, Safari's per-site
  // "Never Auto-Play", data saver). The --light and --fill slots carry no
  // poster, so without this they would be a dead black rectangle with no way in.
  function offerControls(video) {
    if (video.controls) return;
    video.controls = true;
    video.removeAttribute('loop'); // a clip started by hand should end, not cycle
  }

  // 749px is the theme's mobile breakpoint (t330-story.css uses it throughout).
  var MOBILE_QUERY = '(max-width: 749px)';

  function mountVideo(slot) {
    if (slot.__t3sReleased) return null; // released on unload; a stale queued callback must not revive it
    if (slot.__t3sVideo) return slot.__t3sVideo;
    var url = slot.getAttribute('data-video-url');
    if (!url) return null;
    // Phones get the smaller rendition when one has been uploaded. Decided once,
    // here at mount time: swapping src mid-playback on a resize would restart the
    // clip for no benefit, and by now the viewport has settled. matchMedia rather
    // than <source media>, which browsers only evaluate on first load and
    // implement inconsistently for video.
    var mobileUrl = slot.getAttribute('data-video-url-mobile');
    if (mobileUrl && window.matchMedia && window.matchMedia(MOBILE_QUERY).matches) url = mobileUrl;
    var img = slot.querySelector('img');
    var poster = img ? (img.currentSrc || img.src || '') : '';
    var video = document.createElement('video');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'metadata');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('loop', '');
    video.setAttribute('muted', '');
    video.muted = true; // the autoplay policy reads the PROPERTY, not the attribute
    if (poster) video.setAttribute('poster', poster);
    // Tell the CSS what shape this clip actually is. The slot reserves 16:9,
    // which is right for 34 of the 40 clips on these pages and wrong for the
    // multiplex composites (2610x2160, 3840x1080) and the GPS clip (2128x1080);
    // those were being letterboxed into the reserved frame, which put black
    // bars where the slot's rounded corners should have been. Listener is
    // attached before src so it cannot miss the event. preload="metadata"
    // above plus the observer's 150px lead means the ratio normally lands
    // before the slot is on screen.
    video.addEventListener('loadedmetadata', function () {
      if (!video.videoWidth || !video.videoHeight) return;
      slot.style.setProperty('--t3s-video-ar', video.videoWidth + ' / ' + video.videoHeight);
      slot.style.setProperty('--t3s-video-arnum', String(video.videoWidth / video.videoHeight));
    });
    video.src = url;
    // Retire the poster stand-ins only — the caption chip stays where it is.
    if (img && img.parentNode) img.parentNode.removeChild(img);
    var placeholder = slot.querySelector('.t3s-video__placeholder');
    if (placeholder && placeholder.parentNode) placeholder.parentNode.removeChild(placeholder);
    slot.insertBefore(video, slot.firstChild);
    slot.__t3sVideo = video;
    if (prefersReducedMotion()) offerControls(video); // nothing will start it otherwise
    return video;
  }

  // The keyboard control has to describe what the clip is doing right now, not
  // what it was doing when the page rendered — the observers start and stop it
  // without anyone pressing anything.
  function syncPauseButton(slot) {
    var btn = slot.querySelector('[data-video-pause]');
    if (!btn) return;
    var video = slot.__t3sVideo;
    // The accessible name carries the state, so there is deliberately no
    // aria-pressed: a toggle that renames itself AND reports pressed announces
    // "Play video, pressed" while paused, which contradicts itself. Pick one
    // signal — the name is the one a screen reader user acts on.
    var playing = !!video && !video.paused;
    btn.textContent = playing
      ? (btn.getAttribute('data-label-pause') || 'Pause video')
      : (btn.getAttribute('data-label-play') || 'Play video');
  }

  function pauseSlot(slot, byReader) {
    if (slot.__t3sVideo) slot.__t3sVideo.pause();
    if (byReader) slot.__t3sUserPaused = true;
    syncPauseButton(slot);
  }

  function playSlot(slot) {
    var video = mountVideo(slot);
    if (!video) return;
    if (prefersReducedMotion()) return;
    if (slot.__t3sUserPaused) return; // the reader stopped this one on purpose
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function (err) {
        // An AbortError only means we paused it again before playback started
        // (a fast scroll straight past). Only a refusal needs a way in.
        if (!err || err.name === 'NotAllowedError') offerControls(video);
        syncPauseButton(slot);
      });
    }
    syncPauseButton(slot);
  }

  // ------------------------------------------------------------------
  // Observer release. Only the Theme Editor removes a story section from a live
  // document, but there it happens on every re-render, so the leak is unbounded
  // within an editing session rather than a one-off.
  // ------------------------------------------------------------------
  function releaseStoryObservers(root) {
    if (!root || !root.querySelectorAll) return;
    var i;
    var reveals = root.querySelectorAll('.t3s-io');
    for (i = 0; i < reveals.length; i++) {
      if (io) io.unobserve(reveals[i]);
      if (observed) observed.delete(reveals[i]);
    }
    var slots = root.querySelectorAll('.t3s-video[data-video-url]');
    for (i = 0; i < slots.length; i++) {
      if (videoMountIO) videoMountIO.unobserve(slots[i]);
      if (videoPlayIO) videoPlayIO.unobserve(slots[i]);
      if (videoObserved) videoObserved.delete(slots[i]);
      // unobserve() does not discard entries that were already queued, so a slot
      // that crossed a threshold microseconds before the unload can still get a
      // callback afterwards and rebuild a <video> nobody is watching any more.
      // The flag is the gate the callbacks and mountVideo actually check;
      // isConnected is not enough on its own, because the editor fires unload
      // before the node leaves the document. registerVideoAutoplay clears it
      // again if the slot turns out to still be live, so this self-heals.
      slots[i].__t3sReleased = true;
      if (slots[i].__t3sVideo) {
        var v = slots[i].__t3sVideo;
        // Order matters: dropping src alone does not run the media load
        // algorithm, so an in-flight fetch keeps going and currentSrc survives.
        // load() is what aborts it and resets the element to no resource.
        v.pause();
        v.removeAttribute('src');
        v.load();
        if (v.parentNode) v.parentNode.removeChild(v);
        slots[i].__t3sVideo = null;
      }
    }
  }

  function teardownStoryObservers() {
    if (io) { io.disconnect(); io = null; }
    if (videoMountIO) { videoMountIO.disconnect(); videoMountIO = null; }
    if (videoPlayIO) { videoPlayIO.disconnect(); videoPlayIO = null; }
    // Fresh sets: the old ones still hold entries for nodes that are gone, and
    // a rebuilt observer would otherwise skip re-registering their replacements.
    if (typeof WeakSet === 'function') {
      observed = new WeakSet();
      videoObserved = new WeakSet();
    }
  }

  function registerVideoAutoplay() {
    var slots = document.querySelectorAll('.t3s-video[data-video-url]');
    var i;
    if (!slots.length) return;
    // No observer (or no WeakSet to dedupe with): nothing will ever start these,
    // so mount them with controls. Gated-away slots stay untouched — mounting a
    // hidden edition's clips would spend requests on markup nobody can see.
    if (!('IntersectionObserver' in window) || !videoObserved) {
      for (i = 0; i < slots.length; i++) {
        if (hiddenByAttribute(slots[i], null)) continue;
        var mounted = mountVideo(slots[i]);
        if (mounted) offerControls(mounted);
      }
      return;
    }
    // Two observers on purpose. intersectionRatio is measured against the
    // margin-expanded root, so one observer cannot both start fetching early
    // AND gate playback on "enough of it is really on screen" — with a margin
    // the ratio hits 1.0 while the slot is still below the fold.
    if (!videoMountIO) {
      videoMountIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target.__t3sReleased) return;
          if (entry.isIntersecting) mountVideo(entry.target);
        });
      }, {
        // 150px is about two tenths of a second of scrolling -- no clip can be
        // fetched, parsed and decoded in that, so the reader always arrived
        // before the picture did (Bill 2026-09-15). 700px is roughly a
        // viewport of warning on a phone, and it still costs nothing until the
        // reader is heading that way: mounting is what starts the fetch.
        rootMargin: '700px 0px'
      });
    }
    if (!videoPlayIO) {
      videoPlayIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var slot = entry.target;
          if (slot.__t3sReleased) return;
          var onScreen = entry.intersectionRatio >= VIDEO_PLAY_RATIO;
          slot.__t3sInView = onScreen;
          if (onScreen) {
            playSlot(slot);
          } else {
            // A deliberate pause lasts as long as the reader can see the clip;
            // coming back to it later starts fresh rather than finding it frozen.
            slot.__t3sUserPaused = false;
            pauseSlot(slot, false);
          }
        });
      }, { threshold: [0, VIDEO_PLAY_RATIO] });
    }
    for (i = 0; i < slots.length; i++) {
      if (videoObserved.has(slots[i])) continue;
      // Still in the document after a release means the unload was not about
      // this node after all — take the gate back off so it can play again.
      slots[i].__t3sReleased = false;
      videoObserved.add(slots[i]);
      videoMountIO.observe(slots[i]);
      videoPlayIO.observe(slots[i]);
    }
  }

  // ------------------------------------------------------------------
  // Delegated handlers — bound once at document level.
  // ------------------------------------------------------------------
  if (!window.__t3sBound) {
    window.__t3sBound = true;

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      // Panel step arrows: move to the neighbouring VISIBLE pill. Gated pills
      // stay in the DOM with [hidden] on other models, so stepping over the raw
      // NodeList would land on a tab this model cannot see.
      var step = t.closest('[data-t3s-tab-step]');
      if (step) {
        var stepWrap = step.closest('[data-t3s-tabs]');
        if (stepWrap) {
          var all = stepWrap.querySelectorAll('.t3s-tab');
          var live = [];
          for (var s = 0; s < all.length; s++) {
            if (all[s].closest('[data-t3s-tabs]') !== stepWrap) continue;
            if (all[s].hidden) continue;
            live.push(all[s]);
          }
          if (live.length > 1) {
            var at = 0;
            for (var s2 = 0; s2 < live.length; s2++) { if (live[s2].classList.contains('on')) { at = s2; break; } }
            var delta = step.getAttribute('data-t3s-tab-step') === 'prev' ? -1 : 1;
            var to = (at + delta + live.length) % live.length;
            activateTabGroup(stepWrap, live[to], { animate: true, smooth: true });
          }
        }
        return;
      }

      var tab = t.closest('.t3s-tab');
      if (tab) {
        // Compare toggle FIRST: toggle containers use data-t3s-toggle (not
        // data-t3s-tabs) and may be nested inside a tab panel — checking the
        // nearest toggle ancestor first keeps the two handlers from
        // double-firing on one click.
        var toggleWrap = tab.closest('[data-t3s-toggle]');
        if (toggleWrap) {
          setActivePill(toggleWrap, tab);
          var cmpKey = tab.getAttribute('data-tab');
          var cmp = toggleWrap.querySelector('.t3s-cmp');
          if (cmp && cmpKey) {
            var cmpImgs = cmp.querySelectorAll('img[data-cmp]');
            for (var c = 0; c < cmpImgs.length; c++) {
              cmpImgs[c].classList.toggle('on', cmpImgs[c].getAttribute('data-cmp') === cmpKey);
            }
          }
          return;
        }
        // Tabs: pills and panels are paired by string keys (data-tab /
        // data-panel), independent of DOM order.
        var tabsWrap = tab.closest('[data-t3s-tabs]');
        if (tabsWrap) {
          activateTabGroup(tabsWrap, tab, { animate: true, smooth: true });
        }
        return;
      }

      // Layout-diagram switcher: minimal dash row, one pane on at a time.
      var dot = t.closest('.t3s-diagswitch__dot');
      if (dot) {
        var dwrap = dot.closest('[data-t3s-diagrams]');
        if (dwrap) {
          var ddots = dwrap.querySelectorAll('.t3s-diagswitch__dot');
          var idx = -1;
          for (var dd = 0; dd < ddots.length; dd++) { if (ddots[dd] === dot) { idx = dd; break; } }
          setDiagramPane(dwrap, idx);
        }
        return;
      }

      // Accordion: single-open per [data-t3s-acc] group; the active item can
      // swap the group's shared media pane (keyed [data-acc-pane] panes and/or
      // the legacy data-media-src img swap — both via syncAccPane).
      var head = t.closest('.t3s-acc__head');
      if (head) {
        var item = head.closest('.t3s-acc__item');
        var group = head.closest('[data-t3s-acc]');
        if (!item || !group) return;
        if (group.classList.contains('t3s-acc--single')) return; // plain block: nothing to toggle
        if (item.classList.contains('open')) return;
        setOpenAccItem(group, item, { animate: true });
        return;
      }

      // Chrome-free pause target. The clips loop with no controls, so both the
      // slot body (pointer) and the hidden button (keyboard) stop the motion
      // — WCAG 2.2.2 for the motion itself, 2.1.1 for reaching the control.
      // Slots that ended up with native controls are left alone: those handle
      // themselves, and the button is hidden for them in CSS.
      var pauseBtn = t.closest('[data-video-pause]');
      var videoSlot = t.closest('.t3s-video[data-video-url]');
      if (!videoSlot) return;
      var slotVideo = videoSlot.__t3sVideo;
      if (slotVideo && slotVideo.controls) return;
      if (!slotVideo && !pauseBtn) return; // body clicks do nothing before mount
      if (!slotVideo || slotVideo.paused) {
        videoSlot.__t3sUserPaused = false;
        playSlot(videoSlot);
      } else {
        pauseSlot(videoSlot, true);
      }
    });

    // Story tabs use the standard roving-tabindex pattern with horizontal
    // arrow/Home/End keys.
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var tab = t.closest('.t3s-tab');
      var pillScope = tab ? tab.closest(PILL_SCOPE) : null;
      if (tab && pillScope && pillScope.hasAttribute('data-t3s-tabs') &&
          (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End')) {
        var candidates = [];
        var pills = pillScope.querySelectorAll('.t3s-tab');
        for (var i = 0; i < pills.length; i++) {
          if (pills[i].closest(PILL_SCOPE) === pillScope && !hiddenByAttribute(pills[i], pillScope)) candidates.push(pills[i]);
        }
        if (candidates.length) {
          var current = candidates.indexOf(tab);
          var next = current < 0 ? 0 : current;
          if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = candidates.length - 1;
          else if (e.key === 'ArrowRight') next = (next + 1) % candidates.length;
          else if (e.key === 'ArrowLeft') next = (next - 1 + candidates.length) % candidates.length;
          e.preventDefault();
          candidates[next].focus();
          candidates[next].click();
        }
        return;
      }
    });

    // Restoring from the back/forward cache pauses media on the way out but
    // replays no observer entries on the way back, so the clips the reader can
    // see would sit frozen on the last frame until they scroll one clean out
    // and in again. Restart whatever the play observer last saw on screen.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var slots = document.querySelectorAll('.t3s-video[data-video-url]');
      for (var i = 0; i < slots.length; i++) {
        if (slots[i].__t3sInView) playSlot(slots[i]);
      }
    });

    // Theme editor section re-renders do not reload this deferred asset.
    // Re-sync only when the newly inserted section contains a T330/T340TW
    // story module; delegated click/keydown listeners remain bound once.
    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target;
      if (!root || !root.matches) return;
      if (root.matches('.t3s') || root.querySelector('.t3s')) updateStoryGates();
    });

    // Removing a section only fires unload, never load, so without this the
    // chrome observer kept watching the nodes of a section the editor had just
    // deleted — teardownChromeObserver existed but nothing ever reached it.
    // Deleting the LAST story section is the case that matters: once the node is
    // gone, registerChromeObserver finds no roots and tears down.
    //
    // Deferred one frame on purpose. Whether the section is already detached when
    // this event fires is a Theme Editor implementation detail; re-syncing after
    // the current task makes the outcome the same either way, instead of resting
    // on an assumption about removal order.
    document.addEventListener('shopify:section:unload', function (e) {
      var root = e.target;
      if (!root || !root.matches) return;
      if (!root.matches('.t3s') && !root.querySelector('.t3s')) return;
      // An observer keeps observing a target until it is told otherwise, so a
      // merchant re-rendering a story section in the Theme Editor would leave
      // its nodes — and their <video> elements — held by the reveal and video
      // observers for the rest of the session. Release them while we still have
      // the subtree in hand; chromeObserver already had this discipline.
      releaseStoryObservers(root);
      var resync = function () {
        updateStoryGates();
        // Nothing left to watch: drop the observers themselves and start the
        // dedupe sets clean, so a later section:load rebuilds from zero rather
        // than inheriting entries for nodes that no longer exist.
        if (!document.querySelector('.t3s')) teardownStoryObservers();
      };
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(resync);
      else setTimeout(resync, 0);
    });

    window.addEventListener('resize', queueAllPrimaryIndicators, { passive: true });
    // The observer band is keyed to the bar height and viewport, both of which a
    // resize or an orientation change can move. The geometry check inside makes
    // this a no-op when nothing relevant changed.
    window.addEventListener('resize', registerChromeObserver, { passive: true });

    // scroll does not bubble, so catch it in the capture phase: one listener for
    // every rail, present and future, and it only runs when a rail itself moves.
    document.addEventListener('scroll', function (e) {
      var rail = e.target;
      if (rail && rail.classList && rail.classList.contains('t3s-tabs--primary')) syncRailEdges(rail);
    }, true);
  }

  // ------------------------------------------------------------------
  // Init: server-rendered gate state is already correct (harmless re-sync),
  // and registerIO() runs unconditionally so reveal animations still work
  // even when the config anchor is missing or unparsable.
  // ------------------------------------------------------------------
  function init() {
    updateStoryGates();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueAllPrimaryIndicators);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init(); // defer scripts normally land here (readyState 'interactive')
  }
})();
